<template>
  <div class="overflow-auto h-[70vh]">
    <v-data-table
      :headers="tableHeaders"
      :items="processedSalaries"
      :loading="loading"
      loading-text="Loading salary templates..."
      class="table-radius border border-[#e0e0e0]"
      show-select
      disable-sort
    >
      <template v-for="(header, index) in tableHeaders" v-slot:[`header.${header.value}`]>
        <p :key="index" class="font-title" v-tooltip="{ text: header.text }">
          {{ header.text }}
        </p>
      </template>
      <template v-slot:[`item.templateName`]="{ item }">
        <td class="px-4 py-3">{{ item.templateName }}_{{ item?.year }}</td>
      </template>
      <template v-slot:[`item.templateType`]="{ item }">
        <td class="px-4 py-3">
          <v-chip
            small
            :color="
              item.templateType === 'Global'
                ? 'blue lighten-4'
                : item.templateType === 'Branch Global'
                ? 'green lighten-4'
                : 'orange lighten-4'
            "
            :text-color="
              item.templateType === 'Global'
                ? 'blue darken-2'
                : item.templateType === 'Branch Global'
                ? 'green darken-2'
                : 'orange darken-2'
            "
            class="font-weight-medium"
          >
            {{ item.templateType }}
          </v-chip>
        </td>
      </template>
      <template v-slot:[`item.departmentName`]="{ item }">
        <td class="px-4 py-3">{{ getDepartment(item) || '-' }}</td>
      </template>
      <template v-slot:[`item.designationName`]="{ item }">
        <td class="px-4 py-3">{{ getDesignation(item) || '-' }}</td>
      </template>
      <template v-slot:[`item.branchName`]="{ item }">
        <td class="px-4 py-3">{{ item.branchName }}</td>
      </template>

      <template v-slot:[`item.branchAddress`]="{ item }">
        <td class="px-4 py-3">{{ item.branchAddress }}</td>
      </template>

      <!-- <template v-slot:[`item.status`]="{ item }">
        <td class="px-4 py-5 font-semibold">
          <v-chip :color="item.isActive ? 'success' : 'error'" small>
            {{ item.isActive ? 'Active' : 'Inactive' }}
          </v-chip>
        </td>
      </template> -->

      <template v-slot:[`item.actions`]="{ item }">
        <td class="px-4 py-3">
          <ActionButton
            :item="item"
            :can-edit="true"
            :can-delete="true"
            :show-edit-button="true"
            :show-delete-button="true"
            :handle-edit="editTemplate"
            :handle-delete="deleteTemplate"
          />
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="drawer" max-width="900px" persistent>
      <div class="flex flex-col h-[80vh] max-h-[80vh] bg-white">
        <!-- Fixed Header -->
        <div class="sticky top-0 z-10 bg-white">
          <v-card-title class="flex justify-between border-b-2 p-4">
            <h3 class="form-text">{{ currentTemplateId ? 'Edit' : 'Add' }} Salary Template</h3>
            <v-btn icon @click="closeDrawer">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        </div>

        <!-- Scrollable Content -->
        <div class="flex-1 overflow-y-auto">
          <div class="p-4">
            <v-form ref="form">
              <h4 class="section-title mb-4">Basic Information</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <v-text-field
                  label="Template Name"
                  outlined
                  dense
                  hide-details
                  v-model="customTemplateName"
                  :rules="[mandatoryRule]"
                ></v-text-field>
                <v-select
                  v-model="customTemplateYear"
                  :items="yearOptions"
                  label="Select Year"
                  outlined
                  dense
                  hide-details
                  :disabled="currentTemplateId"
                ></v-select>
                <v-autocomplete
                  v-model="selectedBranch"
                  :items="branches"
                  label="Select Branch"
                  item-text="branchName"
                  item-value="_id"
                  outlined
                  dense
                  hide-details
                  clearable
                >
                  <template v-slot:item="{ item }">
                    <div>
                      <div>{{ item.branchName }}</div>
                      <div class="text--secondary caption">{{ item.address }}</div>
                    </div>
                  </template>
                </v-autocomplete>
                <v-autocomplete
                  v-model="selectedDepartment"
                  :items="departments"
                  label="Select Department"
                  item-text="departmentName"
                  item-value="_id"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="fetchDesignationsByDepartment(selectedDepartment)"
                >
                </v-autocomplete
                ><v-autocomplete
                  v-model="selectedDesignation"
                  :items="designations"
                  label="Select Designation"
                  item-text="designationName"
                  item-value="_id"
                  outlined
                  dense
                  hide-details
                  clearable
                >
                </v-autocomplete>
              </div>

              <h4 class="section-title mb-2 mt-4">Fixed Deductions</h4>
              <div class="border border-gray-300 p-2">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <v-text-field
                    v-model="fixedBasicDetails.basic"
                    type="number"
                    suffix="% of CTC"
                    label="Basics"
                    outlined
                    dense
                    disabled
                    hide-details
                  />
                  <v-text-field
                    v-model="fixedBasicDetails.hra"
                    type="number"
                    label="HRA"
                    suffix="% of Basic"
                    outlined
                    dense
                    disabled
                    hide-details
                  />
                  <v-text-field
                    v-model="fixedBasicDetails.professionalTax"
                    type="number"
                    label="Professional Tax"
                    suffix="Yearly"
                    outlined
                    dense
                    hide-details
                  />

                  <v-text-field
                    v-model="fixedBasicDetails.medicalAllowance"
                    type="number"
                    label="Medical Allowance"
                    suffix="Yearly"
                    outlined
                    dense
                    hide-details
                    :rules="[mandatoryRule]"
                  />
                  <v-text-field
                    v-model="fixedBasicDetails.ltaAllowance"
                    label="Leave Travel Allowance"
                    type="number"
                    suffix="Yearly"
                    outlined
                    dense
                    hide-details
                  />
                  <v-text-field
                    v-model="fixedBasicDetails.gratuity"
                    type="number"
                    label="Gratuity"
                    suffix="% of Basic"
                    outlined
                    dense
                    hide-details
                  />
                  <v-text-field
                    v-model="conveyanceAllowance"
                    label="Conveyance Allowance"
                    type="number"
                    suffix="Yearly"
                    outlined
                    dense
                    hide-details
                  />
                </div>
                <h4 class="section-title mb-2 mt-4">PF Deductions</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <v-text-field
                    v-model="pf.employeePf"
                    type="number"
                    label="Employee Contribution"
                    suffix="% of Basic"
                    outlined
                    dense
                    hide-details
                  />
                  <v-text-field
                    v-model="pf.employerPf"
                    type="number"
                    label="Employer Contribution"
                    suffix="% of Basic"
                    outlined
                    dense
                    hide-details
                  />
                </div>
                <h4 class="section-title mb-2 mt-4">Standard Deductions</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <v-text-field
                    v-model="standardDeduction"
                    type="number"
                    label="Standard Deduction"
                    suffix="Fixed"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="[standardDeductionRule]"
                  />
                </div>
              </div>

              <!-- Additional Deductions Section -->
              <div class="mt-6">
                <h4 class="section-title mb-2">Additional Deductions</h4>
                <div class="border border-gray-300 rounded bg-gray-50">
                  <div class="flex justify-between items-center p-3">
                    <div class="text-sm font-medium text-red-900 flex items-center">
                      <v-icon small class="mr-1">mdi-minus-circle-outline</v-icon>
                      Deductions
                    </div>
                    <v-btn
                      x-small
                      color="error"
                      outlined
                      @click="addAdditionalDeduction"
                      class="normal-case font-medium text-xs"
                      v-if="additionalDeductions && additionalDeductions.length < 3"
                    >
                      <v-icon x-small class="mr-1">mdi-plus</v-icon>
                      Add
                    </v-btn>
                  </div>

                  <div class="rounded border-t p-2 bg-gray-50">
                    <div
                      v-for="(deduction, index) in additionalDeductions || []"
                      :key="`deduction-${index}`"
                    >
                      <div class="grid grid-cols-[3fr_2fr_auto] gap-2 pt-2 items-center">
                        <div class="w-full">
                          <v-text-field
                            v-model="deduction.deductionType"
                            label="Deduction Type"
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-tag-outline"
                            placeholder="e.g., Loan Deduction, Insurance"
                            class="text-xs"
                            @input="onAdditionalDeductionsChanged"
                          ></v-text-field>
                        </div>
                        <div class="">
                          <v-text-field
                            v-model.number="deduction.amount"
                            label="Amount(Yearly)"
                            type="number"
                            outlined
                            hide-details
                            dense
                            prefix="₹"
                            placeholder="0"
                            :min="0"
                            @input="onAdditionalDeductionsChanged"
                            class="text-xs"
                          ></v-text-field>
                        </div>
                        <div class="flex items-center justify-end min-h-[32px] relative w-[40px]">
                          <v-btn
                            v-if="additionalDeductions && additionalDeductions.length > 1"
                            icon
                            x-small
                            color="error"
                            @click="removeAdditionalDeduction(index)"
                            class="absolute top-0 right-0 z-10"
                            style="transform: translate(50%, -50%)"
                          >
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="border-t pt-2 mt-2"
                    v-if="additionalDeductions && additionalDeductions.length > 0"
                  >
                    <div class="flex justify-end pr-2">
                      <span class="font-medium text-sm text-red-900">Total Deductions:</span>
                      <span class="font-medium ml-2 text-red-900"
                        >₹{{ totalAdditionalDeductions.toLocaleString() }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tax Saving Investments Section -->
              <div class="mt-6">
                <h4 class="section-title mb-2">Tax Saving Components</h4>
                <div class="border border-gray-300 rounded bg-gray-50">
                  <div class="flex justify-between items-center p-3">
                    <div class="text-sm font-medium text-green-900 flex items-center">
                      <v-icon small class="mr-1">mdi-currency-inr</v-icon>
                      Components
                    </div>
                    <v-btn
                      x-small
                      color="success"
                      outlined
                      @click="addTaxSavingInvestment"
                      class="normal-case font-medium text-xs"
                      v-if="taxSavingInvestments && taxSavingInvestments.length < 5"
                    >
                      <v-icon x-small class="mr-1">mdi-plus</v-icon>
                      Add
                    </v-btn>
                  </div>

                  <div class="rounded border-t p-2 bg-gray-50">
                    <div
                      v-for="(investment, index) in taxSavingInvestments || []"
                      :key="`investment-${index}`"
                    >
                      <div class="grid grid-cols-[2fr_2fr_2fr_auto] gap-2 pt-2 items-center">
                        <div class="w-full">
                          <v-text-field
                            v-model="investment.componentName"
                            label="Investment Type"
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-bank"
                            placeholder="e.g., PPF, ELSS, NSC"
                            class="text-xs"
                            @input="onTaxSavingInvestmentsChanged"
                          ></v-text-field>
                        </div>
                        <div class="">
                          <v-text-field
                            v-model.number="investment.amount"
                            label="Amount(Yearly)"
                            type="number"
                            outlined
                            hide-details
                            dense
                            prefix="₹"
                            placeholder="0"
                            :min="0"
                            @input="onTaxSavingInvestmentsChanged"
                            class="text-xs"
                          ></v-text-field>
                        </div>
                        <div class="flex items-center justify-center">
                          <v-checkbox
                            v-model="investment.documentRequired"
                            label="Document Required"
                            dense
                            hide-details
                            class="mt-0 pt-0 documentRequired"
                          ></v-checkbox>
                        </div>
                        <div class="flex items-center justify-end min-h-[32px] relative w-[40px]">
                          <v-btn
                            v-if="taxSavingInvestments && taxSavingInvestments.length > 1"
                            icon
                            x-small
                            color="error"
                            @click="removeTaxSavingInvestment(index)"
                            class="absolute top-0 right-0 z-10"
                            style="transform: translate(50%, -50%)"
                          >
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="border-t pt-2 mt-2"
                    v-if="taxSavingInvestments && taxSavingInvestments.length > 0"
                  >
                    <div class="flex justify-end pr-2">
                      <span class="font-medium text-sm text-green-900">Total Investments:</span>
                      <span class="font-medium ml-2 text-green-900"
                        >₹{{ totalTaxSavingInvestments.toLocaleString() }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Salary Slip Preview -->
              <div class="mt-8">
                <h4 class="section-title mb-4">Salary Slip Preview</h4>
                <v-text-field
                  v-model="ctc"
                  type="number"
                  label="Employee Yearly Salary"
                  suffix="CTC"
                  outlined
                  dense
                  hide-details
                />
                <SalaryBreakdown
                  :earnings="earnings"
                  :deductions="deductions"
                  :gross-earnings="grossEarnings"
                  :total-deductions="totalDeductions"
                  :net-salary="netSalary"
                />
              </div>
            </v-form>
          </div>
        </div>

        <!-- Fixed Footer -->
        <div class="sticky bottom-0 z-10 bg-white">
          <v-divider></v-divider>
          <div class="flex justify-end p-4">
            <v-btn class="normal-case" text @click="closeDrawer">Cancel</v-btn>
            <v-btn color="primary" class="normal-case" @click="saveDetails">
              <v-icon left>mdi-content-save</v-icon>
              Save</v-btn
            >
          </div>
        </div>
      </div>
    </v-dialog>

    <AlertPopUp
      :showConfirmationDialog="showConfirmationDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="'Are you sure you want to delete the salary template?'"
      :confirmationActionText="'Delete'"
      :confirmationActionColor="'red'"
      :performAction="confirmDelete"
      :cancel="cancelDelete"
    />
  </div>
</template>

<script>
import { convertToTitleCase } from '@/utils/common'
import AlertPopUp from '@/components/AlertPopUp'
import ActionButton from '@/components/ActionButton.vue'
import SalaryBreakdown from '@/components/Workforce/EmployeeManagement/LeaveManagement/SalaryBreakdown.vue'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'SalaryTemplate',
  components: {
    AlertPopUp,
    ActionButton,
    SalaryBreakdown
  },
  props: {
    branches: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      ctc: 2500000,
      drawer: false,
      conveyanceAllowance: null,
      additionalDeductions: [{ deductionType: '', amount: 0 }],
      taxSavingInvestments: [{ componentName: '', amount: 0, documentRequired: false }],
      currentTemplateId: null,
      showConfirmationDialog: false,
      orgSalaries: [],
      loading: true,
      selectedBranch: null,
      templateToDelete: null,
      customTemplateName: '',
      departments: [],
      designations: [],
      standardDeduction: 50000,
      selectedDepartment: null,
      selectedDesignation: null,
      customTemplateYear: new Date().getFullYear(),
      yearOptions: this.generateYearOptions(),
      tableHeaders: [
        { text: 'Template', value: 'templateName', sortable: false },
        { text: 'Type', value: 'templateType', sortable: false },
        { text: 'Department', value: 'departmentName', sortable: false },
        { text: 'Designation', value: 'designationName', sortable: false },
        { text: 'Branch', value: 'branchName', sortable: false },
        { text: 'Address', value: 'branchAddress', sortable: false },
        // { text: 'Status', value: 'status', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  watch: {
    // Watch for changes in branches array
    branches: {
      handler(newVal) {
        // Auto-select first branch if in add mode and no branch is selected
        if (!this.currentTemplateId && newVal && newVal.length > 0 && !this.selectedBranch) {
          this.selectedBranch = newVal[0]._id
        }
      }
    },
    // Watch for changes in departments array
    departments: {
      handler(newVal) {
        // Auto-select first department if in add mode and no department is selected
        if (!this.currentTemplateId && newVal && newVal.length > 0 && !this.selectedDepartment) {
          this.selectedDepartment = newVal[0]._id
          // Fetch designations for the first department
          this.fetchDesignationsByDepartment(this.selectedDepartment)
        }
      },
      immediate: true
    },
    // Watch for changes in designations array
    designations: {
      handler(newVal) {
        // Auto-select first designation if in add mode and no designation is selected
        if (!this.currentTemplateId && newVal && newVal.length > 0 && !this.selectedDesignation) {
          this.selectedDesignation = newVal[0]._id
        }
      }
    }
  },
  computed: {
    mandatoryRule: () => (value) => !!value || 'This field is required',
    positiveNumberRule: (value) => value >= 0 || 'Value must be positive',
    standardDeductionRule() {
      return (value) => {
        if (!value) return true
        if (value < 50000 || value > 75000) {
          return 'Standard Deduction must be between 50,000 and 75,000'
        }
        return true
      }
    },

    processedSalaries() {
      return this.orgSalaries.map((salary) => {
        const branchInfo = this.getBranchInfoById(salary.branch)

        // Determine template type
        let templateType = 'Global'
        if (salary.branch && !salary.department && !salary.designation) {
          templateType = 'Branch Global'
        } else if (salary.branch && salary.department && salary.designation) {
          templateType = 'Designation Specific'
        } else if (!salary.branch && !salary.department && !salary.designation) {
          templateType = 'Global'
        } else {
          // Fallback or other combinations if any
          templateType = 'Custom'
        }

        return {
          ...salary,
          templateName: this.titleCase(salary.template),
          branchName: branchInfo ? branchInfo.branchName : 'N/A',
          branchAddress: branchInfo ? branchInfo.geoFencingData?.formatted_address : 'N/A',
          year: salary.year || '-',
          templateType
        }
      })
    },

    calculatedSpecialAllowance() {
      const basicSalaryPercent = parseFloat(this.fixedBasicDetails.basic) || 0
      const hraPercent = parseFloat(this.fixedBasicDetails.hra) || 0
      const ltaAllowance = parseFloat(this.fixedBasicDetails.ltaAllowance) || 0
      const medicalAllowance = parseFloat(this.fixedBasicDetails.medicalAllowance) || 0
      const conveyanceAllowance = parseFloat(this.conveyanceAllowance) || 0
      const standardDeduction = parseFloat(this.standardDeduction) || 0
      const ctc = parseFloat(this.ctc) || 0
      const taxSavingAmount = parseFloat(this.totalTaxSavingInvestments) || 0

      if (ctc <= 0) {
        return '0.00'
      }

      // Calculate basic salary (40% of CTC)
      const basicSalary = (ctc * basicSalaryPercent) / 100

      // Calculate HRA (50% of Basic)
      const hraAmount = (basicSalary * hraPercent) / 100

      // Calculate special allowance (what's left after deducting all other components from CTC)
      // Subtract tax saving investments from special allowance
      const specialAllowance =
        ctc -
        (basicSalary +
          hraAmount +
          medicalAllowance +
          ltaAllowance +
          conveyanceAllowance +
          taxSavingAmount)

      return specialAllowance >= 0 ? specialAllowance?.toFixed(2) : '0.00'
    },
    netSalary() {
      const grossEarnings = parseFloat(this.grossEarnings)
      const netSalary = grossEarnings - parseFloat(this.totalDeductions)
      return netSalary >= 0 ? netSalary?.toFixed(2) : '0.00'
    },
    fixedBasicDetails() {
      return {
        basic: 40, // 40% of CTC
        hra: 50, // 50% of Basic
        professionalTax: 2400, // Fixed yearly amount
        medicalAllowance: 15000, // Fixed yearly amount
        ltaAllowance: 50000, // This seems to be a default but can be overridden
        gratuity: 4.8 // 4.8% of Basic
      }
    },
    pf() {
      return {
        employeePf: 12, // 12% of Basic
        employerPf: 12 // 12% of Basic
      }
    },
    // Computed properties for salary slip
    earnings() {
      const basicSalaryPercent = parseFloat(this.fixedBasicDetails.basic) || 0
      const hraPercent = parseFloat(this.fixedBasicDetails.hra) || 0
      const ctc = parseFloat(this.ctc) || 0

      if (ctc <= 0) {
        return []
      }

      // Calculate basic salary (40% of CTC)
      const basicSalary = (ctc * basicSalaryPercent) / 100

      // Calculate HRA (50% of Basic)
      const hraAmount = (basicSalary * hraPercent) / 100

      // Calculate special allowance
      const specialAllowance = parseFloat(this.calculatedSpecialAllowance) || 0

      // Calculate LTA
      const ltaAmount = parseFloat(this.fixedBasicDetails.ltaAllowance) || 0

      // Prepare base earnings
      const baseEarnings = [
        { label: 'Basic Salary', amount: parseFloat(basicSalary?.toFixed(2)) },
        { label: 'HRA', amount: parseFloat(hraAmount?.toFixed(2)) },
        {
          label: 'Medical Allowance',
          amount: parseFloat(this.fixedBasicDetails.medicalAllowance?.toFixed(2)) || 0
        },
        { label: 'Leave Travel Allowance', amount: parseFloat(ltaAmount?.toFixed(2)) },
        {
          label: 'Conveyance Allowance',
          amount: parseFloat(this.conveyanceAllowance?.toFixed(2)) || 0
        }
      ]
      const taxSavingItems = (this.taxSavingInvestments || [])
        .filter((saving) => saving.componentName && parseFloat(saving.amount) > 0)
        .map((s) => ({
          label: s.componentName,
          amount: parseFloat(parseFloat(s.amount)?.toFixed(2)) || 0
        }))
      const spAllowance = [
        { label: 'Special Allowance', amount: parseFloat(specialAllowance?.toFixed(2)) }
      ]
      return [...baseEarnings, ...taxSavingItems, ...spAllowance]
    },
    grossEarnings() {
      return parseFloat(
        this.earnings.reduce((total, earning) => total + earning.amount, 0)?.toFixed(2)
      )
    },
    deductions() {
      const basicSalaryPercent = parseFloat(this.fixedBasicDetails.basic) || 0
      const ctc = parseFloat(this.ctc) || 0

      if (ctc <= 0) {
        return []
      }

      // Calculate basic salary (40% of CTC)
      const basicSalary = (ctc * basicSalaryPercent) / 100

      // Calculate employee PF
      const employeePfPercent = parseFloat(this.pf.employeePf) || 0
      const employeePF = (basicSalary * employeePfPercent) / 100

      // Prepare base deductions
      const baseDeductions = [
        { label: 'Employee Provident Fund', amount: parseFloat(employeePF?.toFixed(2)) },
        {
          label: 'Professional Tax',
          amount: parseFloat(this.fixedBasicDetails.professionalTax?.toFixed(2)) || 0
        },
        {
          label: 'Standard Deduction',
          amount: parseFloat(parseFloat(this.standardDeduction)?.toFixed(2)) || 0
        }
      ]

      // Add additional deductions
      const additionalDeductionItems = (this.additionalDeductions || [])
        .filter((deduction) => deduction.deductionType && parseFloat(deduction.amount) > 0)
        .map((deduction) => ({
          label: deduction.deductionType,
          amount: parseFloat(parseFloat(deduction.amount)?.toFixed(2)) || 0
        }))

      // Combine base deductions with additional deductions
      return [...baseDeductions, ...additionalDeductionItems]
    },
    totalDeductions() {
      return parseFloat(
        this.deductions.reduce((total, deduction) => total + deduction.amount, 0)?.toFixed(2)
      )
    },
    totalAdditionalDeductions() {
      return parseFloat(
        (this.additionalDeductions || [])
          .reduce((total, deduction) => total + parseFloat(deduction.amount || 0), 0)
          ?.toFixed(2)
      )
    },
    totalTaxSavingInvestments() {
      return parseFloat(
        (this.taxSavingInvestments || [])
          .reduce((total, investment) => total + parseFloat(investment.amount || 0), 0)
          ?.toFixed(2)
      )
    }
  },
  methods: {
    generateYearOptions() {
      const currentYear = this.$dayjs().year()
      const years = []
      for (let year = currentYear - 1; year <= currentYear + 1; year++) {
        years.push({ text: year.toString(), value: year })
      }
      return years
    },
    getDepartment(item) {
      const department = this.departments.find((d) => d._id === item.department)
      return department?.departmentName || '-'
    },
    getDesignation(item) {
      const designation = this.designations.find((d) => d._id === item.designation)
      return designation?.designationName || '-'
    },
    async fetchDepartments() {
      try {
        const response = await this.$axios.get(axiosUrl.departments.getDepartments)
        this.departments = response.data.departments || []
      } catch (error) {
        console.error('Error fetching departments:', error)
        this.departments = []
      }
    },
    async fetchDesignationsByDepartment(department) {
      try {
        const url = department
          ? `${axiosUrl.designations.getDesignations}?department=${department}`
          : axiosUrl.designations.getDesignations
        const response = await this.$axios.get(url)
        this.designations = response.data.designations || []

        // Auto-select first designation if in add mode and no designation is selected
        if (!this.currentTemplateId && this.designations.length > 0 && !this.selectedDesignation) {
          this.selectedDesignation = this.designations[0]._id
        }
      } catch (error) {
        console.error('Error fetching designations:', error)
        this.designations = []
      }
    },
    titleCase(str) {
      return convertToTitleCase(str)
    },
    getBranchNamesByIds(branchIds) {
      if (!branchIds || !Array.isArray(branchIds) || branchIds.length === 0) {
        return []
      }

      if (!this.branches || this.branches.length === 0) {
        return []
      }

      return branchIds.map((branchId) => {
        const branch = this.branches.find((b) => b._id === branchId)
        return branch ? branch.branchName : 'Unknown Branch'
      })
    },
    getBranchInfoById(branchId) {
      if (!branchId || !this.branches || this.branches.length === 0) return null
      const branch = this.branches.find((b) => b._id === branchId)
      return branch || null
    },
    addAdditionalDeduction() {
      this.additionalDeductions.push({ deductionType: '', amount: 0 })
    },
    removeAdditionalDeduction(index) {
      this.additionalDeductions.splice(index, 1)
      this.onAdditionalDeductionsChanged()
    },
    onAdditionalDeductionsChanged() {
      // Trigger reactivity
      this.$forceUpdate()
    },
    addTaxSavingInvestment() {
      this.taxSavingInvestments.push({ componentName: '', amount: 0, documentRequired: false })
    },
    removeTaxSavingInvestment(index) {
      this.taxSavingInvestments.splice(index, 1)
      this.onTaxSavingInvestmentsChanged()
    },
    onTaxSavingInvestmentsChanged() {
      // Trigger reactivity
      this.$forceUpdate()
    },
    openCreateTemplate() {
      this.resetForm()
      this.standardDeduction = 50000
      this.ctc = 2500000
      this.customTemplateYear = new Date().getFullYear()
      this.fixedBasicDetails = {
        basic: 40, // 40% of CTC
        hra: 50, // 50% of Basic
        professionalTax: 2400, // Fixed yearly amount
        medicalAllowance: 15000, // Fixed yearly amount
        ltaAllowance: 50000, // This seems to be a default but can be overridden
        gratuity: 4.8 // 4.8% of Basic
      }
      this.pf = {
        employeePf: 12, // 12% of Basic
        employerPf: 12 // 12% of Basic
      }
      this.$nextTick(() => {
        if (this.branches && this.branches.length > 0) {
          this.selectedBranch = this.branches[0]._id
        }
        if (this.departments && this.departments.length > 0) {
          this.selectedDepartment = this.departments[0]._id
          this.fetchDesignationsByDepartment(this.selectedDepartment)
        }
        if (this.designations && this.designations.length > 0) {
          this.selectedDesignation = this.designations[0]._id
        }
      })
      this.drawer = true
    },
    closeDrawer() {
      this.drawer = false
      this.resetForm()
    },
    resetForm() {
      this.customTemplateName = ''
      this.selectedBranch = null
      this.conveyanceAllowance = null
      this.additionalDeductions = [{ deductionType: '', amount: 0 }]
      this.taxSavingInvestments = [{ componentName: '', amount: 0, documentRequired: false }]
      this.currentTemplateId = null
      this.standardDeduction = 50000
      this.customTemplateYear = new Date().getFullYear()
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },
    async fetchSalaryTemplates() {
      this.loading = true
      try {
        const response = await this.$axios.get('/org/salary-templates')
        this.orgSalaries = response.data.salaryTemplates || []
      } catch (error) {
        console.error('Error fetching salary templates:', error)
        this.$toast.error('Failed to load salary templates')
        this.orgSalaries = []
      } finally {
        this.loading = false
      }
    },
    async saveDetails() {
      // Validate form
      if (!this.$refs.form.validate()) {
        return
      }

      // Validate required fields
      const requiredFields = [{ field: this.customTemplateName, name: 'Template Name' }]

      for (let { field, name } of requiredFields) {
        if (!field) {
          this.$toast.error(`${name} is required`)
          return
        }
      }

      // Prepare additional deductions - filter out empty ones
      const filteredAdditionalDeductions = (this.additionalDeductions || []).filter(
        (deduction) => deduction.deductionType && deduction.amount > 0
      )

      // Prepare tax saving investments - filter out empty ones
      const filteredTaxSavingInvestments = (this.taxSavingInvestments || []).filter(
        (investment) => investment.componentName && investment.amount > 0
      )

      const payload = {
        template: this.customTemplateName,
        branch: this.selectedBranch,
        designation: this.selectedDesignation,
        department: this.selectedDepartment,
        year: this.customTemplateYear,
        basicSalaryPercent: Number(this.fixedBasicDetails.basic),
        hraPercent: Number(this.fixedBasicDetails.hra),
        ltaPercent: Number(this.fixedBasicDetails.ltaAllowance),
        medicalAllowance: Number(this.fixedBasicDetails.medicalAllowance),
        conveyanceAllowance: Number(this.conveyanceAllowance),
        standardDeduction: Number(this.standardDeduction),
        specialAllowance: Number(this.calculatedSpecialAllowance),
        professionalTax: Number(this.fixedBasicDetails.professionalTax),
        gratuity: Number(this.fixedBasicDetails.gratuity),
        employeePf: Number(this.pf.employeePf),
        employerPf: Number(this.pf.employerPf),
        ctc: Number(this.ctc),
        additionalDeductions: filteredAdditionalDeductions,
        taxSavingComponents: filteredTaxSavingInvestments,
        isActive: true
      }

      // Remove undefined properties
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key]
        }
      })

      try {
        if (this.currentTemplateId) {
          const response = await this.$axios.put(
            `/org/salary-template/${this.currentTemplateId}`,
            payload
          )
          this.$toast.success(response.data.message || 'Salary template updated successfully')
        } else {
          const response = await this.$axios.post('/org/salary-template', payload)
          this.$toast.success(response.data.message || 'Salary template created successfully')
        }

        // Close drawer and refresh data
        this.closeDrawer()
        await this.fetchSalaryTemplates()
      } catch (error) {
        console.error('Error saving salary details:', error)
        const errorMessage = error.response?.data?.message || 'Failed to save salary template'
        this.$toast.error(errorMessage)
      }
    },
    editTemplate(sal) {
      this.currentTemplateId = sal._id
      this.customTemplateName = sal.template
      this.selectedBranch = sal.branch || null
      this.conveyanceAllowance = sal.conveyanceAllowance
      this.standardDeduction = sal.standardDeduction
      this.ctc = sal.ctc || 2500000
      this.selectedDepartment = sal.department || null
      this.fetchDesignationsByDepartment(sal.department)
      this.selectedDesignation = sal.designation || null
      this.customTemplateYear = sal.year || new Date().getFullYear()

      // Populate additional deductions if they exist
      if (sal.additionalDeductions && Array.isArray(sal.additionalDeductions)) {
        this.additionalDeductions = sal.additionalDeductions.map((deduction) => ({
          deductionType: deduction.deductionType || '',
          amount: deduction.amount || 0
        }))
      } else {
        this.additionalDeductions = [{ deductionType: '', amount: 0 }]
      }

      // Populate tax saving investments if they exist
      if (sal.taxSavingComponents && Array.isArray(sal.taxSavingComponents)) {
        this.taxSavingInvestments = sal.taxSavingComponents.map((investment) => ({
          componentName: investment.componentName || '',
          amount: investment.amount || 0,
          documentRequired: investment.documentRequired || false,
          _id: investment?._id || null
        }))
      } else {
        this.taxSavingInvestments = [{ componentName: '', amount: 0, documentRequired: false }]
      }

      this.drawer = true
    },
    deleteTemplate(template) {
      this.templateToDelete = template
      this.showConfirmationDialog = true
    },
    async confirmDelete() {
      if (!this.templateToDelete) return

      try {
        const response = await this.$axios.delete(
          `/org/salary-template/${this.templateToDelete._id}`
        )
        this.$toast.success(response.data.message || 'Salary template deleted successfully')
        await this.fetchSalaryTemplates()
      } catch (error) {
        console.error('Error deleting salary template:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete salary template'
        this.$toast.error(errorMessage)
      } finally {
        this.cancelDelete()
        this.templateToDelete = null
      }
    },
    cancelDelete() {
      this.showConfirmationDialog = false
      this.templateToDelete = null
    },
    numberToWords(num) {
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
      const teens = [
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen'
      ]
      const tens = [
        '',
        '',
        'Twenty',
        'Thirty',
        'Forty',
        'Fifty',
        'Sixty',
        'Seventy',
        'Eighty',
        'Ninety'
      ]

      // Convert to integer to avoid decimal issues
      num = Math.round(parseFloat(num) || 0)

      if (num === 0) return 'Zero'

      function convertHundreds(n) {
        let result = ''
        if (n >= 100) {
          result += ones[Math.floor(n / 100)] + ' Hundred '
          n %= 100
        }
        if (n >= 20) {
          result += tens[Math.floor(n / 10)] + ' '
          n %= 10
        } else if (n >= 10) {
          result += teens[n - 10] + ' '
          return result.trim()
        }
        if (n > 0) {
          result += ones[n] + ' '
        }
        return result.trim()
      }

      let result = ''
      let crore = Math.floor(num / 10000000)
      if (crore > 0) {
        result += convertHundreds(crore) + ' Crore '
        num %= 10000000
      }

      let lakh = Math.floor(num / 100000)
      if (lakh > 0) {
        result += convertHundreds(lakh) + ' Lakh '
        num %= 100000
      }

      let thousand = Math.floor(num / 1000)
      if (thousand > 0) {
        result += convertHundreds(thousand) + ' Thousand '
        num %= 1000
      }

      if (num > 0) {
        result += convertHundreds(num)
      }

      return result.trim()
    }
  },
  async mounted() {
    await this.fetchSalaryTemplates()
    await this.fetchDepartments()
    await this.fetchDesignationsByDepartment()
  }
}
</script>

<style scoped>
/* .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 16px;
  text-align: center;
} */
.form-text {
  font-size: 1.25rem;
  padding: 0px;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
.section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #21427d;
  margin-bottom: 10px;
  align-items: center;
}
/* .text-start {
  text-align: center;
} */
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
.earnings-header {
  background-color: #4a4a4a;
  color: white;
}
.deductions-header {
  background-color: #4a4a4a;
  color: white;
}
.net-salary-box {
  border: 2px solid #4caf50;
  background-color: #f1f8e9;
}
.dotted-line {
  border-bottom: 1px dotted #ccc;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
